import { useState } from "react";
import { CheckCircle2, Circle, MessageSquareText } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PromptJourneyReceipt } from "@/lib/promptJourneyReceipt";
import type { PromptResponseIteration } from "@/types/promptResponse";

const TRUNCATE_LIMIT = 140;

interface PromptJourneyRailProps {
  promptTitle: string;
  promptSummary?: string;
  receipt: PromptJourneyReceipt;
  iterations: PromptResponseIteration[];
}

function ExpandableText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = text.length > TRUNCATE_LIMIT;

  return (
    <p className={className}>
      {expanded || !needsTruncation ? text : `${text.slice(0, TRUNCATE_LIMIT).trim()}...`}
      {needsTruncation && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="ml-1 text-primary hover:text-primary/80 font-medium"
        >
          {expanded ? "see less" : "see more"}
        </button>
      )}
    </p>
  );
}

function RailStep({
  label,
  body,
  complete = true,
  active = false,
  isLast = false,
}: {
  label: string;
  body?: string | null;
  complete?: boolean;
  active?: boolean;
  isLast?: boolean;
}) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className={cn(
            "mt-0.5 flex h-6 w-6 items-center justify-center rounded-full border",
            complete && "border-primary bg-primary text-primary-foreground",
            active && "border-primary bg-background text-primary",
            !complete && !active && "border-border bg-background text-muted-foreground"
          )}
        >
          {complete ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />}
        </div>
        {!isLast && <div className="h-full min-h-4 w-px bg-border" />}
      </div>
      <div className={cn("min-w-0 flex-1", !isLast && "pb-4")}>
        <p className={cn("text-sm font-medium", active ? "text-foreground" : "text-muted-foreground")}>
          {label}
        </p>
        {body && (
          <ExpandableText
            text={body}
            className="mt-1 text-xs leading-relaxed text-muted-foreground/85"
          />
        )}
      </div>
    </div>
  );
}

function RemixDivider() {
  return (
    <div className="flex items-center gap-3 pb-4 pl-9 text-xs font-semibold text-muted-foreground">
      <div className="h-px flex-1 bg-border" />
      <span>Remix</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export function PromptJourneyRail({
  promptTitle,
  promptSummary,
  receipt,
  iterations,
}: PromptJourneyRailProps) {
  const responseSteps = iterations.length === 0
    ? [
        <RailStep
          key="response-empty"
          label="Response"
          body="Paste the model response to continue the journey."
          complete={false}
          active
          isLast
        />,
      ]
    : iterations.flatMap((iteration, index) => {
        const isFinalIteration = index === iterations.length - 1;
        return [
          <RailStep
            key={`${iteration.id}-response`}
            label={`Response ${iteration.turn_index} Pasted`}
            body={iteration.response_summary}
            complete
          />,
          <RailStep
            key={`${iteration.id}-decision`}
            label="Decision Point"
            body={iteration.decision_point}
            complete={Boolean(iteration.decision_point)}
          />,
          <RailStep
            key={`${iteration.id}-specificity`}
            label="Specificity"
            body={iteration.user_specificity_answer || iteration.specificity_question}
            complete={Boolean(iteration.user_specificity_answer)}
            active={!iteration.user_specificity_answer}
          />,
          <RailStep
            key={`${iteration.id}-follow-up`}
            label="Follow-Up Prompt"
            body={iteration.follow_up_summary || iteration.follow_up_prompt}
            complete={Boolean(iteration.follow_up_prompt)}
            active={Boolean(iteration.user_specificity_answer && !iteration.follow_up_prompt)}
            isLast={isFinalIteration}
          />,
        ];
      });

  return (
    <aside className="space-y-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Journey Receipt</p>
        <h3 className="mt-1 text-base font-semibold text-foreground">{promptTitle}</h3>
        {(receipt.objective || promptSummary) && (
          <ExpandableText
            text={(receipt.objective || promptSummary)!}
            className="mt-2 text-xs leading-relaxed text-muted-foreground"
          />
        )}
      </div>

      <div className="rounded-lg border border-border/60 bg-card p-4">
        {receipt.flow === "improved" && (
          <RailStep label="Original Prompt" body={receipt.originalPrompt} complete />
        )}

        {receipt.flow === "remix" && (
          <>
            <RailStep label="Original Prompt" body={receipt.originalPrompt} complete />
            <RemixDivider />
          </>
        )}

        {(receipt.flow === "builder" || receipt.flow === "remix") && (
          <>
            {receipt.builderSteps.map((step) => (
              <RailStep
                key={step.num}
                label={step.label}
                body={step.body}
                complete={step.complete}
              />
            ))}

            {receipt.selectedTool && (
              <RailStep label="Tool Selection" body={receipt.selectedTool} complete />
            )}

            <RailStep
              label="Prompt Created"
              body={receipt.promptCreated}
              complete
            />
          </>
        )}

        {responseSteps}
      </div>

      <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
        <div className="flex items-start gap-2">
          <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-xs leading-relaxed text-muted-foreground">
            This rail is the receipt: what you told Cosmo, what the model returned, and how each follow-up prompt was built.
          </p>
        </div>
      </div>
    </aside>
  );
}
